import { useFormik } from "formik";
import React, { useEffect, useState, useCallback } from "react";
import { Col, Form, Row } from "react-bootstrap";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchGRNDetails,
  createGRN,
  updateGRN,
} from "slices/Warehouse/ItemInward2/GRN/thunk";
import { resetGRNState } from "slices/Warehouse/ItemInward2/GRN/reducer";
import * as Yup from "yup";
import { showError, showConfirm, showValidationErrors } from "utils/swalHelper";
import ConfigureItems from "../ConfigureItemsTables/ConfigureItems";
import { fetchSupplierBydropdown } from "slices/suppliers/thunk";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import { Axios } from "api/axios";
import { InwardType } from "Constants/inwardTypes";
import PageContainer from "components/PageContainer/PageContainer";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { getLoggedinUser } from "helpers/api_helper";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface GRNFormValues {
  inward_date: string;
  invoice_no: string;
  supplier_id: string;
  supplier_name: string;
  warehouse: string;
  warehouse_id: string;
  upload_document: File | null;
  existing_document_file: string | null; // Store existing document URL for edit mode
  remark: string;
  received_by: string;
  received_by_user_id: string;
}

const SupplierGRN: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const dispatch = useDispatch<any>();

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  // Redux state
  const { loading: grnLoading } = useSelector((state: any) => state.grn);

  // Check if we're in edit mode (ID in URL params)
  const inventoryId = params.id;
  const isEdit = Boolean(inventoryId);

  const [grnItems, setGrnItems] = useState<any[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  const validationSchema = Yup.object({
    inward_date: Yup.string().required("Please select Inward Date"),
    invoice_no: Yup.string().required("Please enter Invoice No."),
    supplier_name: Yup.string().required("Please select Supplier Name"),
    warehouse: Yup.string().required("Please select Warehouse"),
    remark: Yup.string(),
    upload_document: Yup.mixed().nullable(),
  });

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      inward_date: isEdit ? "" : getTodayDate(), // Auto-select today's date in add mode
      invoice_no: "",
      supplier_name: "",
      supplier_id: "",
      warehouse: "",
      warehouse_id: "",
      upload_document: null as File | null,
      existing_document_file: null as string | null,
      remark: "",
      received_by: "",
      received_by_user_id: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Reset any previous errors
      dispatch(resetGRNState());

      // ✅ Validate items
      if (grnItems.length === 0) {
        showError("Please add at least one item");
        return;
      }

      // ✅ Validate quantity for gnrdirect type
      const invalidQuantity = grnItems.some((item, index) => {
        if (
          item.quantity === undefined ||
          item.quantity === null ||
          item.quantity <= 0
        ) {
          showError(`Quantity must be greater than 0 for row ${index + 1}`);
          return true;
        }
        return false;
      });

      if (invalidQuantity) return;

      // ✅ Validate rate for items that require rate (not LABOUR_INWARD)
      const invalidRate = grnItems.some((item, index) => {
        if (item.rate === undefined || item.rate === null || item.rate <= 0) {
          showError(`Rate must be greater than 0 for row ${index + 1}`);
          return true;
        }
        return false;
      });

      if (invalidRate) return;

      // ✅ Build FormData
      const formData = buildFormData(values, grnItems);

      try {
        if (isEdit && inventoryId) {
          // Update GRN
          await dispatch(
            updateGRN({ inventory_id: inventoryId, payload: formData })
          ).unwrap();
          toast.success("GRN updated successfully");
        } else {
          // Create GRN
          await dispatch(createGRN(formData)).unwrap();
          toast.success("GRN created successfully");
        }
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        handleSubmitError(err);
      }
    },
  });

  useEffect(() => {
    const fetchSupplierOptions = async () => {
      try {
        const response = await dispatch(fetchSupplierBydropdown()).unwrap();
        console.log("Supplier Options:", response);
        setSupplierOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      }
    };
    fetchSupplierOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchWarehouseOptions = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdown()).unwrap();
        console.log("Warehouse Options:", response);
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
      }
    };
    fetchWarehouseOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        console.log("User Options:", response);
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, []);

  // Auto-select current logged-in user for received_by in create mode
  useEffect(() => {
    // Only auto-select in create mode (not edit mode) and if not already set
    if (!isEdit && userOptions.length > 0 && !formik.values.received_by_user_id) {
      const loggedInUser = getLoggedinUser();
      if (loggedInUser?.user_id) {
        // Find the logged-in user in the userOptions array
        const currentUser = userOptions.find(
          (opt: any) => opt.user_id === loggedInUser.user_id
        );
        
        if (currentUser) {
          formik.setFieldValue("received_by_user_id", currentUser.user_id);
          const fullName =
            currentUser.first_name && currentUser.last_name
              ? `${currentUser.first_name} ${currentUser.last_name}`
              : currentUser.username || currentUser.email || "";
          formik.setFieldValue("received_by", fullName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, isEdit]);

  // Helper function to build FormData
  const buildFormData = useCallback(
    (values: GRNFormValues, items: any[]): FormData => {
      const formData = new FormData();
      formData.append("inward_date", values.inward_date);
      formData.append("invoice_no", values.invoice_no);
      formData.append("remark", values.remark || "");
      formData.append("supplier_id", values.supplier_id || "");
      formData.append("warehouse_id", values.warehouse_id || "");
      formData.append("inward_type", InwardType.SUPPLIER_INWARD); // "Supplier"
      // Note: received_by is included but backend won't process it yet
      formData.append("received_by", values.received_by_user_id || "");

      // Append file if it exists (must be File object, not string)
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      }

      // Transform RowData to GRNItem format
      const transformedItems = items.map((row: any) => {
        const grnItem: any = {
          item_id: row.item_id,
          item_name: row.itemName,
          item_code: row.itemCode,
          category_id: row.category_id,
          category_name: row.category,
          sub_category_id: row.sub_category_id,
          sub_category_name: row.subCategory,
          remarks: row.remarks || null,
          inventory_item_id: row.inventory_item_id, // For edit mode
        };

        const quantity = Number(row.quantity) || 0;
        grnItem.grn_items_details = [
          {
            quantity: quantity,
            selling_price: row.rate ? Number(row.rate) : null,
            remark: row.remarks || null,
          },
        ];

        return grnItem;
      });

      formData.append("grn_items", JSON.stringify(transformedItems));
      return formData;
    },
    []
  );

  // Helper function to handle submit errors
  const handleSubmitError = useCallback((err: any) => {
    const responseData = err?.response?.data || err;
    const validationErrors = responseData?.data?.errors || responseData?.errors;

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try {
        showValidationErrors(validationErrors);
      } catch (swalError) {
        toast.error(validationErrors.join("; "));
      }
    } else {
      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Failed to process GRN. Please try again.";
      toast.error(errorMessage);
    }
  }, []);

  // Fetch GRN data if in edit mode
  useEffect(() => {
    const fetchGRNData = async () => {
      if (!isEdit || !inventoryId || initialDataLoaded) return;

      setIsLoadingInitialData(true);
      try {
        const grnData = await dispatch(fetchGRNDetails(inventoryId)).unwrap();

        if (grnData) {
          // Populate form fields
          formik.setFieldValue(
            "inward_date",
            grnData.inward_date
              ? new Date(grnData.inward_date).toISOString().split("T")[0]
              : ""
          );
          formik.setFieldValue("invoice_no", grnData.invoice_no || "");
          formik.setFieldValue("remark", grnData.remarks || "");

          // Use supplier_details JSON if available, fallback to joined supplier relation
          const supplierData = grnData.supplier_details || grnData.supplier;
          formik.setFieldValue("supplier_id", supplierData?.supplier_id || "");
          formik.setFieldValue(
            "supplier_name",
            supplierData?.supplier_name || ""
          );

          // Use warehouse_details JSON if available, fallback to joined warehouse relation
          const warehouseData = grnData.warehouse_details || grnData.warehouse;
          formik.setFieldValue(
            "warehouse_id",
            warehouseData?.warehouse_id || ""
          );
          formik.setFieldValue(
            "warehouse",
            warehouseData?.warehouse_name || ""
          );

          formik.setFieldValue(
            "existing_document_file",
            grnData.document_file || null
          );

          // Use received_by_details JSON if available, fallback to joined received_by_user relation
          const receivedByData =
            grnData.received_by_details || grnData.received_by_user;
          if (receivedByData) {
            formik.setFieldValue(
              "received_by_user_id",
              receivedByData.user_id || ""
            );
            const fullName =
              receivedByData.first_name && receivedByData.last_name
                ? `${receivedByData.first_name} ${receivedByData.last_name}`
                : receivedByData.username || "";
            formik.setFieldValue("received_by", fullName);
          }

          // Transform inventory_items to RowData format
          if (grnData.inventory_items && grnData.inventory_items.length > 0) {
            const transformedItems = grnData.inventory_items.map(
              (invItem: any, index: number) => {
                const quantity = invItem.quantity || 0;
                const rate = invItem.selling_price || 0;
                const amount = quantity * rate;

                return {
                  id: `edit-${
                    invItem.inventory_item_id || Date.now()
                  }-${index}`,
                  inventory_item_id: invItem.inventory_item_id,
                  item_id: invItem.master_item?.item_id,
                  itemName: invItem.master_item?.item_name,
                  itemCode: invItem.master_item?.item_code,
                  category: invItem.master_item?.category?.category_name,
                  category_id: invItem.master_item?.category?.category_id,
                  subCategory:
                    invItem.master_item?.sub_category?.sub_category_name,
                  sub_category_id:
                    invItem.master_item?.sub_category?.sub_category_id,
                  unit: invItem.master_item?.unit?.unit_name || "",
                  quantity: quantity,
                  rate: rate,
                  amount: amount,
                  remarks: invItem.remark || "",
                };
              }
            );

            setGrnItems(transformedItems);
          }

          setInitialDataLoaded(true);
        }
      } catch (error: any) {
        console.error("Error fetching GRN data:", error);
        toast.error(error?.message || "Failed to load GRN data");
        navigate("/warehouse-item-inward");
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    fetchGRNData();
  }, [isEdit, inventoryId, dispatch, navigate, initialDataLoaded, formik]);

  const handleItemsUpdated = (items: any[]) => {
    setGrnItems(items);
  };

  useEffect(() => {
    console.log("Updated GRN Items: ", grnItems);
  }, [grnItems]);

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit GRN (Supplier)" : "Create GRN (Supplier)"}
        firstLabel="GRN"
        secondLabel={isEdit ? "Edit GRN (Supplier)" : "Create GRN (Supplier)"}
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {isLoadingInitialData ? (
        <LoadingSpinner/>
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            {/* Inward Date */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Invoice No. */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Invoice No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="invoice_no"
                  placeholder="Enter Invoice No."
                  value={formik.values.invoice_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.invoice_no && formik.touched.invoice_no
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.invoice_no}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Supplier Name */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Supplier Name <span className="text-danger">*</span>
                </Form.Label>

                <Autocomplete
                  options={supplierOptions}
                  getOptionLabel={(option: any) => option?.supplier_name || ""}
                  value={
                    supplierOptions.find(
                      (opt: any) =>
                        opt.supplier_name === formik.values.supplier_name
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "supplier_id",
                      selectedOption?.supplier_id || ""
                    );
                    formik.setFieldValue(
                      "supplier_name",
                      selectedOption?.supplier_name || ""
                    );
                    // Clear error if a valid option is selected
                    if (selectedOption?.supplier_name) {
                      formik.setFieldError("supplier_name", undefined);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("supplier_name", true)}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Supplier"
                      name="supplier_name"
                      error={
                        formik.touched.supplier_name &&
                        Boolean(formik.errors.supplier_name)
                      }
                      helperText={
                        formik.touched.supplier_name &&
                        formik.errors.supplier_name
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.supplier_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Warehouse */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Warehouse <span className="text-danger">*</span>
                </Form.Label>
                <WarehouseAutocomplete
                  options={warehouseOptions}
                  value={formik.values.warehouse}
                  valueType="warehouse_name"
                  showWarehouseType={true}
                  formik={formik}
                  formikFieldId="warehouse_id"
                  formikFieldName="warehouse"
                  name="warehouse"
                  disabled={isEdit}
                  inputSx={{
                    background: "#FFF",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                  customSx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                  onChange={(warehouseId, warehouseName) => {
                    formik.setFieldValue("warehouse_id", warehouseId);
                    formik.setFieldValue("warehouse", warehouseName);
                    // Clear error if a valid warehouse is selected
                    if (warehouseName) {
                      formik.setFieldError("warehouse", undefined);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("warehouse", true)}
                />
              </div>
            </Col>

            {/* Received By */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Received By</Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Document */}
             {/* Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document
                              .getElementById("uploadDocInput")
                              ?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue("existing_document_file", null);
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document
                              .getElementById("uploadDocInput")
                              ?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue("existing_document_file", null);
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Remarks */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>

          <hr />

          <ConfigureItems
            inwardType={InwardType.SUPPLIER_INWARD}
            onItemsUpdated={handleItemsUpdated}
            initialItems={isEdit && initialDataLoaded ? grnItems : undefined}
          />

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: isEdit ? "Update GRN" : "Create GRN",
                submitting: isEdit ? "Updating GRN" : "Creating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default SupplierGRN;
